/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ElavonLightBoxCardTypes, ConvergePaymentToken } from '@/lib/elavon/types';

/** How often to check whether Converge's payment global has been attached after script load. */
const GLOBAL_POLL_INTERVAL_MS = 100;
/** Max time to wait for the global before treating the Checkout.js load as failed. */
const GLOBAL_READY_TIMEOUT_MS = 10_000;

/** Approval/decline payload returned by Converge's lifecycle callbacks. */
type ConvergeResponse = Record<string, string>;

/**
 * Converge Checkout.js lifecycle callbacks. The hosted overlay invokes exactly one of these per
 * session. See https://developer.elavon.com (Converge LightBox / Embedded Payment).
 */
interface ConvergeCallbacks {
    onApproval: (response: ConvergeResponse) => void;
    onDeclined: (response: ConvergeResponse) => void;
    onError: (error: ConvergeResponse | string) => void;
    onCancelled: () => void;
}

/** Signature shared by both Converge globals — only the method/global name differs by JS version. */
type ConvergePayFn = (paymentFields: Record<string, unknown>, callbacks: ConvergeCallbacks) => void;

/**
 * Converge ships TWO different hosted-payment products under separate scripts — they are NOT
 * aliases of one another:
 *   - `PayWithConverge.open(...)`  — from `hosted-payments/PayWithConverge.js`. Renders the hosted
 *     card-entry LightBox in a Converge-controlled iframe overlay. THIS is the card-entry modal.
 *   - `ConvergeEmbeddedPayment.pay(...)` — from `hosted-payments/Checkout.js`. Has NO card-entry
 *     form; `pay()` immediately submits a "process" transaction with whatever fields it is given.
 *     Calling it with only a `ccgettoken` session token (no card) returns "Credit Card Number
 *     Invalid" (5000) with no modal — which is why the LightBox must load PayWithConverge.js.
 * Both share the `(paymentFields, callbacks)` signature, so a single type fits either.
 */
declare global {
    interface Window {
        PayWithConverge?: { open: ConvergePayFn };
        ConvergeEmbeddedPayment?: { pay: ConvergePayFn };
    }
}

/**
 * Resolves the Converge card-entry function. Prefers `PayWithConverge.open()` (the hosted
 * card-entry LightBox); only falls through to `ConvergeEmbeddedPayment.pay()` if PayWithConverge
 * is absent. Returns `null` until a global is attached. This ordering is deliberate: the embedded
 * `pay()` has no card form and would auto-process the token, so it must never win when the
 * LightBox global is available.
 */
function resolveConvergePay(): ConvergePayFn | null {
    if (typeof window === 'undefined') return null;
    const lightbox = window.PayWithConverge;
    if (lightbox && typeof lightbox.open === 'function') {
        return lightbox.open.bind(lightbox);
    }
    const embedded = window.ConvergeEmbeddedPayment;
    if (embedded && typeof embedded.pay === 'function') {
        return embedded.pay.bind(embedded);
    }
    return null;
}

export interface ConvergeLightBoxProps {
    /** Session token returned by /action/get-payment-session. Single-use — re-fetch if the basket total changes. */
    sessionToken: string;
    firstName: string;
    lastName: string;
    address: string;
    postalCode: string;
    /** Reference id tied to this session (generated server-side). */
    txnId: string;
    totalAmount: number;
    /** URL of the Converge Checkout.js script — comes from the convergeCheckoutJS site preference. */
    convergeCheckoutUrl: string;
    /** Parsed elavonLightBoxCardTypes JSON for ssl_card_short_description → SFCC card type mapping. */
    cardTypeMappings: ElavonLightBoxCardTypes;
    /** Called when Converge returns a valid ssl_token + card details. */
    onPaymentToken: (data: ConvergePaymentToken) => void;
    /** Called when Converge reports an error or the shopper cancels. */
    onError: (message: string) => void;
    /** Optional label text for the "Pay with Card" trigger button. */
    buttonLabel?: string;
    /**
     * When true, the hosted lightbox opens automatically once Checkout.js is ready — the shopper
     * does not need to click the trigger button. Auto-open fires a single time per mount.
     */
    autoOpen?: boolean;
    /**
     * Called whenever the hosted modal closes WITHOUT capturing a card (shopper cancelled, decline,
     * or error). Converge session tokens are single-use — opening the modal consumes the token, so
     * a second `openLightBox()` with the same token silently fails to render. The parent uses this
     * to mint a fresh session token so the trigger button reliably re-opens a working lightbox.
     */
    onClose?: () => void;
    className?: string;
}

/**
 * Embeds the Converge hosted-payment LightBox for PCI-compliant card entry.
 *
 * The LightBox renders in a Converge-controlled hosted overlay (iframe) — card data never
 * enters our DOM. Flow:
 *   1. Dynamically loads the hosted LightBox script (PayWithConverge.js) from `convergeCheckoutUrl`.
 *   2. When the shopper clicks "Pay with Card" (or on auto-open), calls `PayWithConverge.open()`
 *      with the session token and a lifecycle-callback object. Converge renders the card-entry
 *      iframe overlay. (`ConvergeEmbeddedPayment.pay()` from Checkout.js is a different product
 *      with no card form and is only a last-resort fallback — see resolveConvergePay.)
 *   3. In the `onApproval` callback, maps `ssl_card_short_description` → SFCC card type using
 *      `elavonLightBoxCardTypes`, then invokes `onPaymentToken` so the parent form can
 *      capture the ssl_token in state.
 *
 * Legacy parity:
 *   AngularJS paymentModalCtrl.modalForm + Converge PayWithConverge.js hosted LightBox.
 */
export default function ConvergeLightBox({
    sessionToken,
    firstName,
    lastName,
    // address/postalCode/txnId/totalAmount are intentional API surface for
    // callers, but the current PayWithConverge.js integration only needs the
    // session token (see the `pay()` call below — passing additional ssl_*
    // fields triggers Converge error 5000). Prefixed with `_` to document
    // "accepted but deliberately not consumed here".
    address: _address,
    postalCode: _postalCode,
    txnId: _txnId,
    totalAmount: _totalAmount,
    convergeCheckoutUrl,
    cardTypeMappings,
    onPaymentToken,
    onError,
    buttonLabel,
    autoOpen = false,
    onClose,
    className,
}: ConvergeLightBoxProps) {
    const { t } = useTranslation('checkout');
    const [scriptLoaded, setScriptLoaded] = useState(false);
    const [scriptError, setScriptError] = useState(false);
    const scriptRef = useRef<HTMLScriptElement | null>(null);
    const hasAutoOpenedRef = useRef(false);

    useEffect(() => {
        const cancelled = false;
        let pollTimer: ReturnType<typeof setInterval> | undefined;
        let timeoutTimer: ReturnType<typeof setTimeout> | undefined;

        const clearTimers = () => {
            if (pollTimer) clearInterval(pollTimer);
            if (timeoutTimer) clearTimeout(timeoutTimer);
        };

        // The script's `load` event fires when the file is fetched + executed, but Converge's
        // Checkout.js does not always have its payment global attached at that exact tick. Poll
        // until the global actually exists before reporting the module ready — otherwise the
        // autoOpen effect fires `openLightBox()` against an undefined global and surfaces a
        // spurious "Payment module not ready" error. Only after a real timeout do we treat it as a
        // genuine load failure.
        const waitForGlobal = () => {
            if (cancelled) return;
            if (resolveConvergePay()) {
                setScriptLoaded(true);
                return;
            }
            pollTimer = setInterval(() => {
                if (cancelled) return;
                if (resolveConvergePay()) {
                    clearTimers();
                    setScriptLoaded(true);
                }
            }, GLOBAL_POLL_INTERVAL_MS);
            timeoutTimer = setTimeout(() => {
                if (cancelled) return;
                clearTimers();
                if (!resolveConvergePay()) {
                    setScriptError(true);
                    onError(t('payment.converge.errorScriptLoad'));
                }
            }, GLOBAL_READY_TIMEOUT_MS);
        };

        // Checkout.js already loaded by a previous mount — the global persists, so we're ready.
        if (resolveConvergePay()) {
            setScriptLoaded(true);
            return;
        }

        // No usable script URL — fail fast with a precise error instead of injecting an empty-src
        // <script> (which loads the current page, never registers the global, and would only
        // surface an error after the readiness timeout elapses). A blank URL means the Converge
        // checkoutUrl is configured in neither the Shopper Configurations API nor config defaults.
        if (!convergeCheckoutUrl || !/^https:\/\//i.test(convergeCheckoutUrl)) {
            setScriptError(true);
            onError(t('payment.converge.errorScriptLoad'));
            return;
        }

        // Script tag already injected by this mount (e.g. StrictMode re-run) — wait for the global.
        if (scriptRef.current) {
            waitForGlobal();
            return clearTimers;
        }

        const script = document.createElement('script');
        script.src = convergeCheckoutUrl;
        script.async = true;

        script.onload = () => waitForGlobal();
        script.onerror = () => {
            setScriptError(true);
            onError(t('payment.converge.errorScriptLoad'));
        };

        scriptRef.current = script;
        document.body.appendChild(script);

        // Script removal on unmount is intentionally skipped — Converge Checkout.js
        // registers global handlers that must persist for the LightBox callback to fire
        // after the shopper closes the hosted overlay. Only the readiness timers are torn down.
        return clearTimers;
    }, [convergeCheckoutUrl, onError, t]);

    /**
     * Maps ssl_card_short_description to an SFCC card type via the `elavonLightBoxCardTypes`
     * mapping. Falls back to a title-cased version of the raw Converge value when no
     * mapping is found.
     */
    const mapCardType = useCallback(
        (sslDescription: string): string => {
            for (const mapping of Object.values(cardTypeMappings)) {
                if (mapping && mapping.sslDescription === sslDescription) {
                    return mapping.SFCCCardType;
                }
            }
            return sslDescription.charAt(0).toUpperCase() + sslDescription.slice(1).toLowerCase();
        },
        [cardTypeMappings]
    );

    const openLightBox = useCallback(() => {
        const pay = resolveConvergePay();
        if (!pay) {
            onError(t('payment.converge.errorNotReady'));
            return;
        }

        // Pass ONLY the session token. The token (created server-side via the transaction_token
        // endpoint as a `ccgettoken`) already carries the transaction type, amount, and merchant
        // details, and the hosted modal collects the card. Passing additional ssl_* fields (amount,
        // AVS, names) makes Converge attempt a DIRECT authorization with no card number — which
        // returns errorCode 5000 "Credit Card Number Invalid" and never renders the card-entry
        // modal. Matches legacy LibElavonPayment + the Converge reference integration.
        pay(
            {
                ssl_txn_auth_token: sessionToken,
            },
            {
                onApproval: (response) => {
                    const sslToken = response.ssl_token;
                    if (!sslToken) {
                        onError(t('payment.converge.errorNoToken'));
                        return;
                    }

                    const sslCardShortDescription = response.ssl_card_short_description ?? '';
                    const expDate = response.ssl_exp_date ?? '';
                    const expirationMonth = expDate.length >= 2 ? parseInt(expDate.slice(0, 2), 10) : 0;
                    // Keep the SCAPI-layer semantic 4-digit — every other paymentCard.expirationYear
                    // reader (payment summary UI, saved-card flow, `submit-payment.server.ts` non-LightBox
                    // CC path) treats this as a 4-digit year. The legacy 2-digit `<expiration-year>26</expiration-year>`
                    // shape in the exported order XML is produced downstream inside
                    // `ElavonAuthorizationHelper` by re-stamping `creditCardExpirationYear` right before
                    // `setCreditCardToken` freezes the payment-instrument fields — same pattern as legacy
                    // `LibElavonPayment.js:105` which was defensive and accepted either digit form.
                    const expirationYear = expDate.length === 4 ? 2000 + parseInt(expDate.slice(2, 4), 10) : 0;

                    const cardHolder = `${firstName} ${lastName}`.trim();
                    const cardType = mapCardType(sslCardShortDescription);

                    onPaymentToken({
                        sslToken,
                        cardType,
                        cardHolder,
                        expirationMonth,
                        expirationYear,
                        sslCardShortDescription,
                        transactionInfo: response,
                    });
                },
                onDeclined: (response) => {
                    const code = response.ssl_result ?? '';
                    onError(response.ssl_result_message ?? t('payment.converge.errorDeclined', { code }));
                    // Token was consumed by the declined attempt — mint a fresh one for retry.
                    onClose?.();
                },
                onError: (error) => {
                    const message =
                        typeof error === 'string'
                            ? error
                            : (error?.errorMessage ?? error?.errorName ?? t('payment.converge.errorScriptLoad'));
                    onError(message);
                    onClose?.();
                },
                // Shopper closed the hosted overlay without paying. Not an error — but the token is
                // now spent, so signal the parent to mint a fresh one for the next open.
                onCancelled: () => {
                    onClose?.();
                },
            }
        );
    }, [sessionToken, firstName, lastName, mapCardType, onPaymentToken, onError, onClose, t]);

    // Auto-open the hosted lightbox once Checkout.js is ready. Guarded by a ref so it fires a
    // single time per mount — re-opening automatically after the shopper closes/cancels would trap
    // them in a loop. Re-selecting Credit Card remounts this component, which resets the guard.
    useEffect(() => {
        if (!autoOpen || !scriptLoaded || hasAutoOpenedRef.current) return;
        hasAutoOpenedRef.current = true;
        openLightBox();
    }, [autoOpen, scriptLoaded, openLightBox]);

    if (scriptError) {
        return <p className="text-sm text-destructive">{t('payment.converge.scriptError')}</p>;
    }

    // The button opens the hosted modal with the current (fresh) session token. After the modal
    // closes without capturing a card, `onClose` lets the parent mint a fresh token so the next
    // click opens a working lightbox again (the token is single-use).
    return (
        <button
            type="button"
            onClick={openLightBox}
            disabled={!scriptLoaded}
            className={className}
            aria-busy={!scriptLoaded}>
            {buttonLabel ?? t('payment.converge.payWithCard')}
        </button>
    );
}
